import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

import { makeLogger } from "helpers/pwa_logger";
import { isPwa, isPushSupported, getPushManager, getSubscription } from "helpers/pwa_utils";
import { randomBase58 } from "random";

const log = makeLogger("push_subscription");

export default class extends Controller {
  static values = {
    notificationTestPath: String,
    railsEnv: String,
    scope: String,
    subscriptionPath: String,
    testEnabled: Boolean,
    vapidKeyBytes: Array,
    animationDuration: { type: Number, default: 500 },
  };
  static targets = [
    "subscribeBanner",
    "testBanner",
    "notificationsOverlay",
    "notificationsPanel",
    "notificationsCloseButton",
    "notificationsTitle",
    "notificationsDescription",
    "notificationsSubscribeButton",
    "pushSubscriptionContainer",
  ];

  async connect() {
    log("push_subscription_controller connected");
    log("Targets available:", {
      hasNotificationsDescription: this.hasNotificationsDescriptionTarget,
      hasNotificationsPanel: this.hasNotificationsPanelTarget,
      hasNotificationsSubscribeButton: this.hasNotificationsSubscribeButtonTarget,
    });
    this.#showBanners();
  }

  async #showBanners() {
    let isRunningAsPwa = await isPwa();
    if (this.railsEnvValue === "test") {
      // TODO: Get rid of this once we have native-environment tests
      isRunningAsPwa = true;
    }

    if (isRunningAsPwa && isPushSupported()) {
      log("Showing notification banners");
      const subscription = await getSubscription();
      if (subscription) {
        log("Already subscribed - not showing test banner");
      } else {
        log("Not subscribed - showing subscribe banner");
        this.pushSubscriptionContainerTarget.classList.remove("hidden");
        this.subscribeBannerTarget.classList.remove("hidden");
      }
    } else {
      log(
        `Not showing notification banners. isPwa=${isRunningAsPwa} isPushSupported=${isPushSupported()}`
      );
    }
  }

  showNotificationsGuide() {
    log("Showing notifications guide");
    this.notificationsOverlayTarget.classList.remove("pointer-events-none", "opacity-0");
    requestAnimationFrame(() => {
      this.notificationsPanelTarget.classList.remove("translate-y-full");
    });
  }

  closeNotificationsGuide() {
    log("Closing notifications guide");
    this.notificationsPanelTarget.classList.add("translate-y-full");
    setTimeout(() => {
      this.notificationsOverlayTarget.classList.add("opacity-0", "pointer-events-none");
    }, this.animationDurationValue);
    this.subscribeBannerTarget.classList.add("hidden");
    this.pushSubscriptionContainerTarget.classList.add("hidden");
  }

  async subscribe() {
    try {
      // Request permission to send notifications.
      // If permission has already been granted then this will return right away.
      const permission = await Notification.requestPermission();
      log("Permission returned:", permission);
      if (permission !== "granted") {
        log("Notification permission not granted");
        this.notificationsSubscribeButtonTarget.classList.add("hidden");
        this.notificationsTitleTarget.textContent = "Notifications couldn't be enabled";
        this.notificationsDescriptionTarget.textContent =
          "To enable them, please go to your settings, find this app, and allow notifications. You will then be able to receive important updates and offers.";
        this.notificationsCloseButtonTarget.textContent = "Close";
        throw new Error("Notification permission denied");
      }

      // Create a subscription via the browser's push service (e.g. with Apple, Google)
      // If the browser has already generated a subscription then this will return right away.
      const pushManager = await getPushManager();
      this.#stubInTest(pushManager);
      const subscription = await pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: new Uint8Array(this.vapidKeyBytesValue),
      });
      log("Subscription:", subscription);

      // Send the subscription to the server
      const response = await post(this.subscriptionPathValue, {
        body: JSON.stringify({
          scope: this.scopeValue,
          subscription: subscription.toJSON(),
        }),
        responseKind: "turbo-stream",
      });
      await this.#validateResponse(response);

      // Close the guide
      this.closeNotificationsGuide();
    } catch (error) {
      const uniqueId = randomBase58(6);
      log(`Subscription failed ${uniqueId}: ${error}`);
      this.subscribeBannerTarget.textContent = `Failed to subscribe. Error code: ${uniqueId}`;

      // Remove the push subscription
      try {
        const subscription = await getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          log("Successfully unsubscribed after error");
        }
      } catch (unsubError) {
        log(`Failed to unsubscribe after error: ${unsubError}`);
      }
    }
  }

  async #validateResponse(response) {
    if (!response.ok) {
      const body = await response.text;
      const requestId = response.headers.get("X-Request-Id");
      throw new Error(
        `Request failed with ${response.statusCode} for ${requestId} - ${body.slice(0, 500)}`
      );
    }
  }

  #stubInTest(pushManager) {
    // Avoid actually creating subscriptions on Google's infra during specs
    if (this.railsEnvValue === "test") {
      pushManager.subscribe = async () => {
        return {
          toJSON: () => ({
            endpoint: "https://fake-push-endpoint.rinsed.example.dev",
            keys: {
              auth: "fake-auth",
              p256dh: "fake-p256dh",
            },
          }),
        };
      };
    }
  }
}
